import type { CardDefinition, TokenTemplate } from './types.ts';

/**
 * Pure-TypeScript Deck-Limited Paper Card Recognition Engine (Phase 8).
 *
 * Key Architectural Advantage:
 * Instead of matching webcam frames against 30,000+ Magic printings on a cloud server,
 * Playtable scopes recognition on-device strictly to the seat's loaded deck + tokens +
 * registered alters (typically 60–100 unique cards), combining:
 *   1. 64-bit Perceptual Difference Hashing (`dHash` over a 9x8 luminance grid)
 *   2. Card-Title OCR / fuzzy string similarity (`titleHint`)
 *
 * Zero video frames are ever stored on the server, and custom alter registrations store
 * only `{ cardName, artistCredit, dHashHex }` — never external image uploads!
 */

export interface DeckRecognitionPoolEntry {
  cardName: string;
  scryfallId: string;
  typeLine: string;
  oracleText: string;
  imageUrl: string;
  isToken: boolean;
  referenceDHashHex: string;
  alterArtistCredit?: string | null;
}

export interface RecognitionCandidate {
  cardName: string;
  scryfallId: string;
  typeLine: string;
  oracleText: string;
  imageUrl: string;
  isToken: boolean;
  confidence: number;
  hammingDistance: number;
  matchedVia: 'alter_dhash' | 'card_dhash' | 'title_ocr' | 'hybrid';
  alterArtistCredit?: string | null;
}

export interface DeckScopedRecognitionResult {
  topMatch: RecognitionCandidate | null;
  candidates: RecognitionCandidate[];
  /** True when top confidence < 0.58 or an OCR hint strongly diverges from every card in the loaded deck */
  isOffListWarning: boolean;
  poolSizeChecked: number;
}

/**
 * Computes a 64-bit horizontal gradient difference hash (`dHash`) as a 16-char lowercase hex string
 * from a 9-column × 8-row = 72-sample grayscale luminance array (`0..255`).
 */
export function computeGrayscaleDHash64(luminance9x8: ArrayLike<number>): string {
  if (luminance9x8.length < 72) {
    throw new Error('computeGrayscaleDHash64 requires at least 72 luminance samples (9x8 grid).');
  }

  let bits = 0n;
  for (let row = 0; row < 8; row++) {
    const rowOffset = row * 9;
    for (let col = 0; col < 8; col++) {
      const left = Number(luminance9x8[rowOffset + col]) || 0;
      const right = Number(luminance9x8[rowOffset + col + 1]) || 0;
      bits = (bits << 1n) | (left < right ? 1n : 0n);
    }
  }
  return bits.toString(16).padStart(16, '0');
}

export function isValidDHashHex(hex: string): boolean {
  return /^[0-9a-f]{16}$/i.test(hex.trim());
}

/**
 * Computes the exact bitwise Hamming distance (`0..64`) between two 16-hex-char 64-bit dHashes.
 */
export function hammingDistance64(hashHexA: string, hashHexB: string): number {
  const cleanA = hashHexA.trim().toLowerCase();
  const cleanB = hashHexB.trim().toLowerCase();
  if (!isValidDHashHex(cleanA) || !isValidDHashHex(cleanB)) {
    return 64;
  }
  let diff = BigInt(`0x${cleanA}`) ^ BigInt(`0x${cleanB}`);
  let count = 0;
  while (diff > 0n) {
    diff &= diff - 1n;
    count++;
  }
  return count;
}

/**
 * Deterministic baseline 64-bit perceptual hash derived from canonical card identity
 * (used when browser canvas artwork rasterization isn't cached yet or in headless tests).
 */
export function computeDeterministicCardArtHash(cardName: string, scryfallId = ''): string {
  const seed = `${cardName.trim().toLowerCase()}::${scryfallId.trim().toLowerCase()}`;
  let h1 = 0x811c9dc5;
  let h2 = 0x9e3779b9;
  for (let i = 0; i < seed.length; i++) {
    const ch = seed.charCodeAt(i);
    h1 ^= ch;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= ch + ((i * 131) & 0xff);
    h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

function normalizeTitle(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Computes normalized title similarity (`0..1`) combining prefix/substring bonus and edit distance.
 */
export function computeCardTitleSimilarity(ocrHint: string, candidateName: string): number {
  const a = normalizeTitle(ocrHint);
  const b = normalizeTitle(candidateName);
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (b.startsWith(a) && a.length >= 4) {
    return Math.min(0.96, 0.78 + (a.length / b.length) * 0.18);
  }

  const m = a.length;
  const n = b.length;
  const dp = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;

  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + cost);
      prev = temp;
    }
  }

  const maxLen = Math.max(m, n);
  return Math.max(0, 1 - dp[n] / maxLen);
}

/**
 * Builds the deck-scoped recognition pool for a seat from its unique deck cards,
 * tokens, and custom alter perceptual hashes.
 */
export function buildDeckRecognitionPool(params: {
  deckCards: CardDefinition[];
  tokens?: TokenTemplate[];
  registeredAlters?: { cardName: string; artistCredit: string; dHashHex: string }[];
  customCardHashes?: Record<string, string>;
}): DeckRecognitionPoolEntry[] {
  const seen = new Set<string>();
  const pool: DeckRecognitionPoolEntry[] = [];

  for (const card of params.deckCards) {
    const key = card.name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const customHash = params.customCardHashes?.[key];
    pool.push({
      cardName: card.name,
      scryfallId: card.scryfallId,
      typeLine: card.typeLine,
      oracleText: card.oracleText,
      imageUrl: card.imageUrl,
      isToken: false,
      referenceDHashHex:
        customHash && isValidDHashHex(customHash)
          ? customHash.toLowerCase()
          : computeDeterministicCardArtHash(card.name, card.scryfallId),
    });
  }

  for (const tok of params.tokens ?? []) {
    const key = `token:${tok.name.trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    pool.push({
      cardName: tok.name,
      scryfallId: `token_${tok.name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`,
      typeLine: tok.typeLine,
      oracleText: tok.oracleText,
      imageUrl: tok.imageUrl,
      isToken: true,
      referenceDHashHex: computeDeterministicCardArtHash(tok.name, tok.typeLine),
    });
  }

  // Add registered custom alters/proxies as explicit entries linked to their official deck card!
  for (const alter of params.registeredAlters ?? []) {
    if (!isValidDHashHex(alter.dHashHex)) continue;
    const base = pool.find(
      (p) => !p.isToken && p.cardName.toLowerCase() === alter.cardName.trim().toLowerCase()
    );
    if (!base) continue;
    pool.push({
      ...base,
      referenceDHashHex: alter.dHashHex.trim().toLowerCase(),
      alterArtistCredit: alter.artistCredit,
    });
  }

  return pool;
}

/**
 * Matches a single scanned card region (`frameDHashHex` and/or `ocrTitleHint`) strictly against
 * the seat's deck-limited recognition pool (`O(deckSize)`).
 */
export function recognizeCardFromDeckPool(
  pool: readonly DeckRecognitionPoolEntry[],
  observation: {
    frameDHashHex?: string | null;
    ocrTitleHint?: string | null;
  },
  maxCandidates = 5
): DeckScopedRecognitionResult {
  const hasHash = Boolean(observation.frameDHashHex && isValidDHashHex(observation.frameDHashHex));
  const hasOcr = Boolean(observation.ocrTitleHint && observation.ocrTitleHint.trim().length >= 2);

  if (pool.length === 0 || (!hasHash && !hasOcr)) {
    return {
      topMatch: null,
      candidates: [],
      isOffListWarning: Boolean(hasHash || hasOcr),
      poolSizeChecked: pool.length,
    };
  }

  const bestByCardKey = new Map<string, RecognitionCandidate>();

  for (const entry of pool) {
    const dist = hasHash
      ? hammingDistance64(observation.frameDHashHex!, entry.referenceDHashHex)
      : 64;
    const hashSim = hasHash ? Math.max(0, 1 - dist / 64) : 0;
    const ocrSim = hasOcr
      ? computeCardTitleSimilarity(observation.ocrTitleHint!, entry.cardName)
      : 0;

    let confidence = 0;
    let matchedVia: RecognitionCandidate['matchedVia'] = 'card_dhash';

    if (hasHash && hasOcr) {
      // Hybrid fusion: strong match in either channel boosts confidence, agreement reaches ~0.99
      confidence = Math.max(hashSim * 0.92, ocrSim * 0.92, hashSim * 0.55 + ocrSim * 0.45);
      matchedVia = entry.alterArtistCredit ? 'alter_dhash' : 'hybrid';
    } else if (hasHash) {
      confidence = hashSim;
      matchedVia = entry.alterArtistCredit ? 'alter_dhash' : 'card_dhash';
    } else {
      confidence = ocrSim;
      matchedVia = 'title_ocr';
    }

    const dedupeKey = `${entry.isToken ? 'tok' : 'card'}:${entry.cardName.toLowerCase()}`;
    const existing = bestByCardKey.get(dedupeKey);
    if (!existing || confidence > existing.confidence) {
      bestByCardKey.set(dedupeKey, {
        cardName: entry.cardName,
        scryfallId: entry.scryfallId,
        typeLine: entry.typeLine,
        oracleText: entry.oracleText,
        imageUrl: entry.imageUrl,
        isToken: entry.isToken,
        confidence: Number(confidence.toFixed(4)),
        hammingDistance: dist,
        matchedVia,
        alterArtistCredit: entry.alterArtistCredit ?? null,
      });
    }
  }

  const ranked = Array.from(bestByCardKey.values())
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, maxCandidates);

  const topMatch = ranked[0] ?? null;
  const isOffListWarning = !topMatch || topMatch.confidence < 0.58;

  return {
    topMatch,
    candidates: ranked,
    isOffListWarning,
    poolSizeChecked: pool.length,
  };
}

/**
 * Multi-card row scan for tutor reveals, Cascade piles, or opening playmat sweeps from a single high-res still frame.
 */
export function recognizeMultiCardRowFromPool(
  pool: readonly DeckRecognitionPoolEntry[],
  observations: readonly { frameDHashHex?: string | null; ocrTitleHint?: string | null }[]
): DeckScopedRecognitionResult[] {
  return observations.map((obs) => recognizeCardFromDeckPool(pool, obs, 3));
}

/**
 * Validates a custom alter / proxy registration for a paper seat.
 * Enforces mandatory artist attribution and rejects any external image URLs.
 */
export function validateAlterRegistration(params: {
  cardName: string;
  artistCredit: string;
  dHashHex: string;
  deckCardNames: readonly string[];
}): { ok: true; canonicalCardName: string; artistCredit: string; dHashHex: string } | { ok: false; error: string } {
  const cardName = params.cardName.trim();
  const artistCredit = params.artistCredit.trim();
  const dHashHex = params.dHashHex.trim().toLowerCase();

  if (!cardName) {
    return { ok: false, error: 'Card name is required to register a custom alter or proxy.' };
  }
  if (!artistCredit || artistCredit.length < 2) {
    return {
      ok: false,
      error: 'Artist credit attribution is required when registering a custom alter or proxy.',
    };
  }
  if (/^(https?:|data:|blob:)/i.test(artistCredit) || /^(https?:|data:|blob:)/i.test(dHashHex)) {
    return {
      ok: false,
      error: 'External image URLs are never stored for alters — only the on-device 64-bit perceptual hash is saved.',
    };
  }
  if (!isValidDHashHex(dHashHex)) {
    return {
      ok: false,
      error: 'Alter perceptual hash must be a 16-character hexadecimal 64-bit dHash.',
    };
  }

  const matchedName = params.deckCardNames.find(
    (n) => n.trim().toLowerCase() === cardName.toLowerCase()
  );
  if (!matchedName) {
    return {
      ok: false,
      error: `"${cardName}" is not in your loaded deck. Add it via Paper Deck Update first if playing it in paper.`,
    };
  }

  return {
    ok: true,
    canonicalCardName: matchedName,
    artistCredit: artistCredit.slice(0, 120),
    dHashHex,
  };
}
